import { useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import { apiBatch } from '@/api/batch';
import type {
    Ledger,
    Collection,
    Tag,
    LedgerValue,
    PaginatedResponse,
} from '@/api/types';

const VALUES_PAGE_SIZE = 20;

/**
 * Ledger with expanded collection and tags from the updated backend endpoint.
 */
export interface ExpandedLedger extends Ledger {
    collection: Collection;
    tags: Tag[];
}

export interface LedgerDetailBatchData {
    ledger: ExpandedLedger;
    initialValues: PaginatedResponse<LedgerValue> & { userLatestConfirmationDate: number | null };
}

/**
 * Hook to fetch ledger detail data in a single batch request.
 * Combines ledger (with expanded collection/tags) and initial values.
 * Seeds the infinite query cache so useLedgerValues can use the batch data.
 */
export function useLedgerDetailBatch(ledgerId: number) {
    const queryClient = useQueryClient();

    return useQuery({
        queryKey: ['ledger-detail-batch', ledgerId],
        queryFn: async (): Promise<LedgerDetailBatchData> => {
            const result = await apiBatch<{
                ledger: ExpandedLedger;
                values: PaginatedResponse<LedgerValue> & { userLatestConfirmationDate: number | null };
            }>([
                { id: 'ledger', path: `/api/ledgers/${ledgerId}` },
                { id: 'values', path: `/api/ledgers/${ledgerId}/values?limit=${VALUES_PAGE_SIZE}&offset=0&order=DESC` },
            ]);

            const ledgerData = result.responses.ledger.data;
            const valuesData = result.responses.values.data;

            if (result.responses.ledger.status === 404) {
                throw new ApiError(404, 'Ledger not found');
            }
            if (!ledgerData) {
                throw new ApiError(500, 'Ledger data missing');
            }

            // Seed the infinite query cache so useLedgerValues finds pre-fetched data
            if (valuesData) {
                const queryKey = ['ledgers', ledgerId, 'values'];
                const existingData = queryClient.getQueryData(queryKey);
                if (!existingData) {
                    queryClient.setQueryData<InfiniteData<PaginatedResponse<LedgerValue>>>(
                        queryKey,
                        { pages: [valuesData], pageParams: [0] }
                    );
                }
            }

            return {
                ledger: ledgerData,
                initialValues: valuesData!,
            };
        },
        enabled: !!ledgerId,
        staleTime: 30000, // 30 seconds
    });
}
